import {
  FeatureFlagsContext,
  GraphDataQualityWarning,
  GraphQLErrorCode,
  reportGraphQLError,
  SiteUrlsContext,
  WarningStatusChip,
  WarningStatusIcon,
  WarningStatusProps
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Query } from 'react-apollo';

import {
  EmbeddedDatasourceLineageUpstreamDataQualityWarningsQuery,
  EmbeddedDatasourceLineageUpstreamDataQualityWarningsQueryVariables
} from '../../../gen-src/queries/EmbeddedDatasourceLineageUpstreamDataQualityWarningsQuery';

import {
  EmbeddedDatasourceLineageUpstreamDataQualityWarningsQueryGql
} from '../../queries/EmbeddedDatasourceLineageUpstreamDataQualityWarningsQueryGql';
import { renderQueryResult } from '../renderQueryResult';

export interface EmbeddedDatasourceDataQualityWarningProps {
  readonly nodeId: string;
  readonly iconOnly: boolean;
}

type WarningQuery = EmbeddedDatasourceLineageUpstreamDataQualityWarningsQuery;
type WarningVariables = EmbeddedDatasourceLineageUpstreamDataQualityWarningsQueryVariables;

// Passes props so that the popover dialog shows up on the left since the vizclient pane always appears on the right
// until we can port the sophisticated render side selection algorithm in ChromeTooltipWidget
export function EmbeddedDatasourceDataQualityWarning(props: EmbeddedDatasourceDataQualityWarningProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const urls = React.useContext(SiteUrlsContext);
  const renderers = React.useMemo(() => {
    return {
      renderData: renderWarnings(urls, props.iconOnly),
      renderDataWithError: renderWarningsWithError(urls, props.iconOnly)
    };
  }, [urls, props.iconOnly]);

  return (
    <Query<WarningQuery, WarningVariables>
      query={EmbeddedDatasourceLineageUpstreamDataQualityWarningsQueryGql}
      variables={{ embeddedDatasourceId: props.nodeId, first: 10, offset: 0, publishedConnectionsLineageEnabled }}
    >
      {result => renderQueryResult(result, renderers)}
    </Query>
  );
}

const empty: GraphDataQualityWarning[] = [];

function renderWarningsWithError(urls: SiteUrls, iconOnly: boolean): (data: WarningQuery, errorCode: GraphQLErrorCode | null) => React.ReactNode {
  return (data: WarningQuery, errorCode: GraphQLErrorCode | null): React.ReactNode => {
    // Ignore the PERMISSIONS_MODE_SWITCHED error, which should not block Data Details from being shown
    if (errorCode === null || errorCode === 'PERMISSIONS_MODE_SWITCHED') {
      return renderWarnings(urls, iconOnly)(data);
    } else {
      return reportGraphQLError(errorCode); // Behave as renderQueryResult would have
    }
  };
}

function renderWarnings(urls: SiteUrls, iconOnly: boolean): (data: WarningQuery) => JSX.Element | null {
  return (data: WarningQuery): JSX.Element | null => {
    const workbook = data.embeddedDatasources ? data.embeddedDatasources[0] : null;
    if (!workbook) {
      return null;
    }

    const props: WarningStatusProps = {
      subjectArea: 'EmbeddedDatasource',
      kind: 'graph',
      side: 'left',
      anchoredAsset: null,
      anchoredWarnings: [],
      iconOnly,
      urls,
      upstreamWarnings: workbook.upstreamDataQualityWarningsConnection?.nodes ?? empty,
    }

    return iconOnly ? <WarningStatusIcon {...props} /> : <WarningStatusChip {...props} />;
  }
}
